import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeleteStore } from '@/lib/api/stores';

const DELETION_ITEM_KEYS = [
  'customers',
  'orders',
  'subscriptions',
  'payments',
  'checkouts',
  'funnels',
  'storefront',
  'catalog',
  'marketing',
  'integrations',
  'clubs',
  'security',
  'activity',
] as const;

interface DeleteStoreBoxProps {
  storeId: string;
  storeName: string;
  onDeleted: () => void;
}

export function DeleteStoreBox({ storeId, storeName, onDeleted }: DeleteStoreBoxProps) {
  const { t } = useTranslation(['stores']);
  const [open, setOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const deleteStore = useDeleteStore();

  const trimmedName = storeName.trim();
  // Case-insensitive: the name is a confirmation, not a password. An operator typing
  // `staging store` for "Staging Store" was silently left with a dead button (#TAG-2649).
  const nameMatches =
    typed.trim().localeCompare(trimmedName, undefined, { sensitivity: 'accent' }) === 0 && trimmedName.length > 0;
  const showMismatch = typed.trim().length > 0 && !nameMatches;
  const isPending = deleteStore.isPending;
  const canConfirm = nameMatches && !isPending;

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    setOpen(next);
    if (!next) {
      setTyped('');
      setDetailsOpen(false);
    }
  };

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Radix's AlertDialogAction auto-closes on click — we drive the flow ourselves.
    event.preventDefault();
    if (!canConfirm) return;
    try {
      await deleteStore.mutateAsync({ storeId });
      setOpen(false);
      onDeleted();
    } catch {
      // useDeleteStore already fires an error toast; keep the modal open for retry.
    }
  };

  return (
    <>
      <Card className="border-2 border-red-500/70 bg-red-50/30 dark:bg-red-950/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="size-5" />
            {t('deleteBox.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-red-700 dark:text-red-400">{t('deleteBox.description')}</p>
          <Button
            type="button"
            variant="destructive"
            className="w-full"
            onClick={() => setOpen(true)}
          >
            <Trash2 className="size-4" />
            {t('deleteBox.button')}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="size-5" />
              {t('deleteBox.dialog.title', { storeName: trimmedName })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('deleteBox.dialog.warning')}</AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            <Button
              type="button"
              variant="ghost"
              className="h-8 w-full justify-between px-2 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setDetailsOpen((v) => !v)}
            >
              <span>
                {detailsOpen
                  ? t('deleteBox.dialog.hideDetails')
                  : t('deleteBox.dialog.showDetails', { count: DELETION_ITEM_KEYS.length })}
              </span>
              {detailsOpen ? (
                <ChevronUp className="size-4" />
              ) : (
                <ChevronDown className="size-4" />
              )}
            </Button>

            {detailsOpen && (
              <ul className="max-h-56 list-disc space-y-1 overflow-y-auto rounded-md border border-red-200 bg-red-50/40 py-2 pl-8 pr-3 text-xs text-red-800 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300">
                {DELETION_ITEM_KEYS.map((key) => (
                  <li key={key}>{t(`deleteBox.dialog.items.${key}`)}</li>
                ))}
              </ul>
            )}

            <div className="space-y-2">
              <Label htmlFor="delete-store-confirm" className="text-sm">
                <Trans
                  i18nKey="stores:deleteBox.dialog.typePrompt"
                  values={{ storeName: trimmedName }}
                  components={{ strong: <strong className="font-semibold" /> }}
                />
              </Label>
              <Input
                id="delete-store-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={t('deleteBox.dialog.typePlaceholder')}
                disabled={isPending}
                autoComplete="off"
                aria-invalid={showMismatch || undefined}
                aria-describedby={showMismatch ? 'delete-store-confirm-mismatch' : undefined}
              />
              {showMismatch && (
                <p id="delete-store-confirm-mismatch" className="text-xs text-red-700 dark:text-red-400">
                  {t('deleteBox.dialog.typeMismatch', { storeName: trimmedName })}
                </p>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>
              {t('deleteBox.dialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              {isPending && <Loader2 className="size-4 animate-spin" />}
              {isPending ? t('deleteBox.dialog.deleting') : t('deleteBox.dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
